import { isNotNullNotUndefined } from "./utilities.js";
function decodeXmlEntities(text) {
  return text.replace(
    /&(#(?:x[0-9a-f]+|\d+)|amp|lt|gt|quot|apos);/gi,
    (entity, value) => {
      const normalizedValue = value.toLowerCase();
      switch (normalizedValue) {
        case "amp":
          return "&";
        case "lt":
          return "<";
        case "gt":
          return ">";
        case "quot":
          return '"';
        case "apos":
          return "'";
        default: {
          const isHex = normalizedValue.startsWith("#x");
          const rawCodePoint = value.slice(isHex ? 2 : 1);
          const codePoint = Number.parseInt(rawCodePoint, isHex ? 16 : 10);
          if (Number.isNaN(codePoint) || codePoint < 0 || codePoint > 1114111) {
            return entity;
          }
          return String.fromCodePoint(codePoint);
        }
      }
    }
  );
}
function extractChildElement(xml, elementName) {
  const regex = new RegExp(
    `<${elementName}>([\\s\\S]*?)</${elementName}>`,
    "i"
  );
  const captured = xml.match(regex)?.[1];
  return captured !== void 0 ? decodeXmlEntities(captured.trim()) : void 0;
}
function extractChildElements(xml, elementName) {
  const regex = new RegExp(
    `<${elementName}>([\\s\\S]*?)</${elementName}>`,
    "gi"
  );
  const matches = [];
  let match;
  while ((match = regex.exec(xml)) !== null) {
    const value = match[1] ? decodeXmlEntities(match[1].trim()) : void 0;
    if (value) {
      matches.push(value);
    }
  }
  return matches;
}
function unescapeXmlContent(text) {
  return text.replace(/\\(["n\\])/g, (_escaped, ch) => {
    if (ch === "n") return "\n";
    return ch;
  });
}
function extractAttribute(openingTag, attributeName) {
  const regex = new RegExp(`${attributeName}="([^"]*)"`, "i");
  const captured = openingTag.match(regex)?.[1];
  return captured !== void 0 ? decodeXmlEntities(captured) : void 0;
}
function parseUpdateKnowledgeBaseDocument(openingTag, content) {
  const name = extractAttribute(openingTag, "name");
  if (!isNotNullNotUndefined(name)) {
    return null;
  }
  const find = extractChildElement(content, "find");
  const replace = extractChildElement(content, "replace");
  if (!isNotNullNotUndefined(find) || !isNotNullNotUndefined(replace)) {
    return null;
  }
  return {
    type: "update-knowledge-base-document",
    documentName: name,
    patch: {
      find: unescapeXmlContent(find),
      replace: unescapeXmlContent(replace)
    }
  };
}
function parseAddChapter(content) {
  const topic = extractChildElement(content, "topic");
  const lessons = parseNestedLessonDrafts(content);
  if (!topic && lessons.length === 0) {
    return null;
  }
  return {
    type: "add-chapter",
    topic,
    lessons: lessons.length > 0 ? lessons : void 0
  };
}
function parseLessonType(lessonType) {
  if (lessonType === "chat-quiz" || lessonType === "knowledge-test" || lessonType === "role-play") {
    return lessonType;
  }
  return null;
}
function parseLessonDraftContent(openingTag, content) {
  const lessonType = parseLessonType(extractAttribute(openingTag, "type"));
  if (!lessonType) {
    return null;
  }
  const questions = extractChildElements(content, "question");
  return {
    lessonType,
    title: extractChildElement(content, "title"),
    topic: extractChildElement(content, "topic"),
    questions: questions.length > 0 ? questions : void 0,
    participantInstructions: extractChildElement(
      content,
      "participant_instructions"
    ),
    agentInstructions: extractChildElement(content, "agent_instructions")
  };
}
function parseNestedLessonDrafts(content) {
  const lessonRegex = /<lesson([^>]*)>([\s\S]*?)<\/lesson>/gi;
  const lessons = [];
  let lessonMatch;
  while ((lessonMatch = lessonRegex.exec(content)) !== null) {
    const lesson = parseLessonDraftContent(
      `<lesson${lessonMatch[1]}>`,
      lessonMatch[2] ?? ""
    );
    if (lesson) {
      lessons.push(lesson);
    }
  }
  return lessons;
}
function parseUpdateChapter(openingTag, content) {
  const id = extractAttribute(openingTag, "id");
  const topic = extractChildElement(content, "topic");
  if (!id || !topic) {
    return null;
  }
  return {
    type: "update-chapter",
    id,
    topic
  };
}
function parseRemoveChapter(openingTag) {
  const id = extractAttribute(openingTag, "id");
  const name = extractAttribute(openingTag, "name");
  if (!id || !name) {
    return null;
  }
  return {
    type: "remove-chapter",
    id,
    name
  };
}
function parseMoveChapter(openingTag) {
  const id = extractAttribute(openingTag, "id");
  const targetChapterId = extractAttribute(openingTag, "target_id");
  const position = extractAttribute(openingTag, "position");
  if (!id || !targetChapterId || position !== "before" && position !== "after") {
    return null;
  }
  return {
    type: "move-chapter",
    id,
    targetChapterId,
    position
  };
}
function parseAddLesson(openingTag, content) {
  const chapterId = extractAttribute(openingTag, "chapter_id");
  const lessonDraft = parseLessonDraftContent(openingTag, content);
  if (!chapterId || !lessonDraft) {
    return null;
  }
  return {
    type: "add-lesson",
    chapterId,
    ...lessonDraft
  };
}
function parseUpdateLesson(openingTag, content) {
  const id = extractAttribute(openingTag, "id");
  if (!id) {
    return null;
  }
  const title = extractChildElement(content, "title");
  const topic = extractChildElement(content, "topic");
  const questions = extractChildElements(content, "question");
  const participantInstructions = extractChildElement(
    content,
    "participant_instructions"
  );
  const agentInstructions = extractChildElement(content, "agent_instructions");
  if (!title && !topic && questions.length === 0 && !participantInstructions && !agentInstructions) {
    return null;
  }
  return {
    type: "update-lesson",
    id,
    title,
    topic,
    questions: questions.length > 0 ? questions : void 0,
    participantInstructions,
    agentInstructions
  };
}
function parseRemoveLesson(openingTag) {
  const id = extractAttribute(openingTag, "id");
  const name = extractAttribute(openingTag, "name");
  if (!id || !name) {
    return null;
  }
  return {
    type: "remove-lesson",
    id,
    name
  };
}
function parseMoveLesson(openingTag) {
  const id = extractAttribute(openingTag, "id");
  const targetChapterId = extractAttribute(openingTag, "target_chapter_id");
  const targetLessonId = extractAttribute(openingTag, "target_lesson_id");
  const position = extractAttribute(openingTag, "position");
  if (!id || !position || !["before", "after", "end"].includes(position) || (position === "end" ? !targetChapterId : !targetLessonId)) {
    return null;
  }
  return {
    type: "move-lesson",
    id,
    targetChapterId,
    targetLessonId,
    position
  };
}
const TAG_PATTERNS = {
  // Knowledge base document tags
  "update-knowledge-base-document": /<update-knowledge-base-document([^>]*)>([\s\S]*?)<\/update-knowledge-base-document>/gi,
  // Learning journey tags
  "add-chapter": /<add-chapter([^>]*)>([\s\S]*?)<\/add-chapter>/gi,
  "update-chapter": /<update-chapter([^>]*)>([\s\S]*?)<\/update-chapter>/gi,
  "remove-chapter": /<remove-chapter([^>]*?)\/>/gi,
  "move-chapter": /<move-chapter([^>]*?)\/>/gi,
  "add-lesson": /<add-lesson([^>]*)>([\s\S]*?)<\/add-lesson>/gi,
  "update-lesson": /<update-lesson([^>]*)>([\s\S]*?)<\/update-lesson>/gi,
  "remove-lesson": /<remove-lesson([^>]*?)\/>/gi,
  "move-lesson": /<move-lesson([^>]*?)\/>/gi
};
function findAllTags(text) {
  const matches = [];
  for (const [tagType, pattern] of Object.entries(TAG_PATTERNS)) {
    pattern.lastIndex = 0;
    let match;
    while ((match = pattern.exec(text)) !== null) {
      const openingTagOrAttributes = match[1];
      if (openingTagOrAttributes === void 0) {
        continue;
      }
      matches.push({
        tagType,
        fullMatch: match[0],
        openingTagOrAttributes,
        content: match[2],
        // undefined for self-closing tags
        startIndex: match.index,
        endIndex: match.index + match[0].length
      });
    }
  }
  return matches.sort((a, b) => a.startIndex - b.startIndex);
}
function parseTagMatch(match) {
  const mockOpeningTag = `<${match.tagType}${match.openingTagOrAttributes}>`;
  switch (match.tagType) {
    case "update-knowledge-base-document":
      return parseUpdateKnowledgeBaseDocument(
        mockOpeningTag,
        match.content || ""
      );
    case "add-chapter":
      return parseAddChapter(match.content || "");
    case "update-chapter":
      return parseUpdateChapter(mockOpeningTag, match.content || "");
    case "remove-chapter":
      return parseRemoveChapter(mockOpeningTag);
    case "move-chapter":
      return parseMoveChapter(mockOpeningTag);
    case "add-lesson":
      return parseAddLesson(mockOpeningTag, match.content || "");
    case "update-lesson":
      return parseUpdateLesson(mockOpeningTag, match.content || "");
    case "remove-lesson":
      return parseRemoveLesson(mockOpeningTag);
    case "move-lesson":
      return parseMoveLesson(mockOpeningTag);
    default:
      return null;
  }
}
function parseConfigurationAgentMessage(text) {
  const segments = [];
  const tagMatches = findAllTags(text);
  if (tagMatches.length === 0) {
    if (text.trim()) {
      return [{ type: "text", id: "text-0", text }];
    }
    return [];
  }
  let currentIndex = 0;
  for (const match of tagMatches) {
    if (match.startIndex > currentIndex) {
      const textBefore = text.slice(currentIndex, match.startIndex);
      if (textBefore.trim()) {
        segments.push({
          type: "text",
          id: `text-${currentIndex}`,
          text: textBefore
        });
      }
    }
    const operation = parseTagMatch(match);
    if (operation) {
      segments.push({
        type: "operation",
        id: `op-${match.startIndex}`,
        operation
      });
    } else {
      segments.push({
        type: "text",
        id: `text-${match.startIndex}`,
        text: match.fullMatch
      });
    }
    currentIndex = match.endIndex;
  }
  if (currentIndex < text.length) {
    const textAfter = text.slice(currentIndex);
    if (textAfter.trim()) {
      segments.push({
        type: "text",
        id: `text-${currentIndex}`,
        text: textAfter
      });
    }
  }
  return segments;
}
function getOperationLabel(operation) {
  switch (operation.type) {
    case "update-knowledge-base-document":
      return "Update knowledge base article";
    case "add-chapter":
      return "Add chapter";
    case "update-chapter":
      return "Update chapter";
    case "remove-chapter":
      return "Remove chapter";
    case "move-chapter":
      return "Move chapter";
    case "add-lesson":
      return "Add lesson";
    case "update-lesson":
      return "Update lesson";
    case "remove-lesson":
      return "Remove lesson";
    case "move-lesson":
      return "Move lesson";
  }
}
function getOperationCategory(operation) {
  switch (operation.type) {
    case "add-chapter":
    case "add-lesson":
      return "add";
    case "update-knowledge-base-document":
    case "update-chapter":
    case "move-chapter":
    case "update-lesson":
    case "move-lesson":
      return "update";
    case "remove-chapter":
    case "remove-lesson":
      return "remove";
  }
}
function isLearningJourneyOperation(operation) {
  return operation.type === "add-chapter" || operation.type === "update-chapter" || operation.type === "remove-chapter" || operation.type === "move-chapter" || operation.type === "add-lesson" || operation.type === "update-lesson" || operation.type === "remove-lesson" || operation.type === "move-lesson";
}
function isKnowledgeBaseDocumentOperation(operation) {
  return operation.type === "update-knowledge-base-document";
}
export {
  getOperationCategory,
  getOperationLabel,
  isKnowledgeBaseDocumentOperation,
  isLearningJourneyOperation,
  parseConfigurationAgentMessage
};
//# sourceMappingURL=configurationAgentParser.js.map
